/**
 * Pure decision logic for how `GameHostPage` should present a TanStack Query
 * session-poll result (docs/games/word-rush/GAME_PLAN.md §11: "A poll
 * failure ... shows a non-destructive 'reconnecting' banner ... never a
 * blank screen or a lost board").
 *
 * Bug fix + regression coverage (CLAUDE.md §6): the previous `GameHostPage`
 * rendered `ErrorState` (replacing the entire page) whenever
 * `sessionQuery.isError` was true, with no check for whether `data` was
 * still available from an earlier successful fetch. TanStack Query v5 sets
 * `status: 'error'` on a *background* refetch failure while leaving the
 * previous `data` in place (it does not revert to `'pending'` or clear
 * `data`), so a single dropped poll — a routine, non-destructive network
 * blip while a board is already showing — wiped the player's entire board
 * and timer and replaced it with a full-page error, for every game that
 * uses this shared platform component (not only Word Rush). Extracted as a
 * pure function, matching this codebase's convention
 * (`lib/session-outcome.ts`) of keeping presentation decisions
 * unit-testable without a DOM/query-client harness.
 */
export type SessionConnectionState = 'loading' | 'blocking-error' | 'ok' | 'reconnecting';

export interface SessionQueryLikeState {
  isPending: boolean;
  isError: boolean;
  hasData: boolean;
}

export function describeSessionConnectionState(
  query: SessionQueryLikeState,
): SessionConnectionState {
  if (query.hasData) {
    return query.isError ? 'reconnecting' : 'ok';
  }
  return query.isPending ? 'loading' : 'blocking-error';
}
